import React from "react";
import { CheckCircle, Circle, LockKey, Path } from "@phosphor-icons/react";

/**
 * RoadmapStrip — compact horizontal "you are here" visualization.
 * Shows all stages of a playbook as ordered chips:
 *   ✓ completadas · ● actual · 🔒 futuras
 *
 * Mobile-first: only a short label ("Fundación", "Etapa 1") renders per chip
 * to prevent horizontal overflow. The full stage title should be rendered
 * elsewhere (e.g. inside the active stage card).
 *
 * Props:
 *   stages: [{ id, name, status }]  — same shape as playbook.stages
 *   currentIndex: number            — index of the current active stage
 *   onStageClick?: (index, stage) => void  (optional — for jump-to-stage anchoring)
 *   size?: "sm" | "md" (default "md")
 */

// Threshold beyond which we fall back to the generic "Etapa N" label.
// Keeps every chip a single line on 360px viewports.
const SHORT_LABEL_MAX = 12;

function shortLabel(name, index) {
  if (!name) return `Etapa ${index + 1}`;
  // Take the first "word cluster" before any separator that a user might use.
  const trimmed = String(name).trim();
  const firstPart = trimmed.split(/[:·\-–—]/)[0].trim() || trimmed;
  if (firstPart.length <= SHORT_LABEL_MAX) return firstPart;
  return `Etapa ${index + 1}`;
}

export default function RoadmapStrip({ stages = [], currentIndex = 0, onStageClick, size = "md", testid }) {
  if (!stages.length) return null;
  const isSm = size === "sm";
  const allDone = stages.every((s) => s.status === "completada");

  return (
    <div className="w-full" data-testid={testid || "roadmap-strip"}>
      <div className="flex items-center gap-2 mb-2 label-mono text-muted-foreground">
        <Path size={12} weight="bold" />
        Recorrido
      </div>
      <div className="flex items-stretch gap-0 border-2 border-foreground bg-surface overflow-hidden">
        {stages.map((s, i) => {
          const isPast = allDone || i < currentIndex;
          const isCurrent = !allDone && i === currentIndex;
          const isFuture = !allDone && i > currentIndex;
          const Icon = isPast ? CheckCircle : isFuture ? LockKey : Circle;
          const bg = isCurrent ? "hsl(var(--primary))" : isPast ? "hsl(var(--foreground))" : "transparent";
          const fg = isCurrent || isPast ? "hsl(var(--primary-foreground))" : "hsl(var(--muted-foreground))";
          const label = shortLabel(s.name, i);
          return (
            <button
              key={s.id || i}
              type="button"
              onClick={() => onStageClick?.(i, s)}
              disabled={!onStageClick}
              data-testid={`roadmap-stage-${i}`}
              data-state={isCurrent ? "current" : isPast ? "past" : "future"}
              title={s.name}
              className={`flex-1 min-w-0 border-r-2 border-foreground last:border-r-0 ${
                isSm ? "px-1 py-2" : "px-1.5 py-2.5"
              } flex flex-col items-center justify-center gap-1 text-center transition-colors ${onStageClick ? "hover:opacity-90 cursor-pointer" : "cursor-default"}`}
              style={{ background: bg, color: fg }}
            >
              <Icon size={isSm ? 12 : 16} weight={isCurrent ? "fill" : "bold"} className="flex-shrink-0" />
              <div className="min-w-0 w-full">
                <div className={`font-semibold ${isSm ? "text-[9px]" : "text-[10px]"} leading-tight truncate`}>
                  {label}
                </div>
                <div className={`label-mono ${isSm ? "text-[7px]" : "text-[8px]"} opacity-80 mt-0.5 truncate`}>
                  {isPast ? "OK" : isCurrent ? "Actual" : "Próx."}
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
